import {FieldArray, Formik} from 'formik';
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';

import {Section} from 'components/layouts';
import api from 'utils/api';
import {gettext} from 'utils/text';
import Checkboxes from './Checkboxes';
import FormGroup from './FormGroup';
import SelectField from './SelectField';


const componentKey = index => `components.${index}`;
const orderKey = id => `order.${id}`;
const componentFieldName = (field, index) => `${componentKey(index)}.${field}`;
const getComponentFieldError = (errors, field, index) => {
    if (!errors.components) return null;
    if (index >= errors.components.length) return null;
    return errors.components[index][field] || null;
};
const isComponentFieldTouched = (touched, field, index) => {
    if (!touched.components) return false;
    if (!touched.components[index]) return false;
    return !!touched.components[index][field];
};

const formLayout = (productId, welderRole, readOnly) => (layoutProps) => {
    const {
        values, errors, touched, handleChange, handleBlur, handleSubmit,
        setFieldTouched, setFieldValue, isSubmitting,
    } = layoutProps;

    let nonFieldErrors = null;
    if (errors.nonFieldErrors) {
        nonFieldErrors = errors.nonFieldErrors.map((error, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="alert alert-danger" key={`non-field-error-${i}`}>{error}</div>
        ));
    }

    return (
        <form onSubmit={handleSubmit}>
            {values.related && values.related.length > 0 &&
            <Section title={gettext("Similar products")}>
                {values.related.map((order, index) => (
                    <div key={order.id}>
                        <div className="form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id={orderKey(order.id)}
                                name={`related[${index}].is_checked`}
                                checked={order.is_checked}
                                onChange={handleChange}
                            />
                            <label className="form-check-label" htmlFor={orderKey(order.id)}>{order.label}</label>
                        </div>
                    </div>
                ))}
            </Section>
            }
            <h4 className="mb-4">{gettext("Product details")}</h4>
            {nonFieldErrors}
            <div className="form-row">
                <SelectField
                    label={gettext("Welder")}
                    name="welder"
                    value={values.welder}
                    cssClass="col-md-4"
                    loadOptions={(input, callback) => {
                        api.accountsUserList.fetch(null, {role: welderRole, search: input}).then(
                            (data) => {
                                callback(null, {
                                    options: data.results.map(user => ({label: user.name, value: user.id})),
                                    complete: true,
                                });
                            },
                            console.error,
                        );
                    }}
                    touched={!!touched.welder}
                    error={errors.welder}
                    onBlur={setFieldTouched}
                    onChange={setFieldValue}
                />
                <Checkboxes
                    checkboxes={[
                        {
                            name: "requires_finishing",
                            label: gettext("Requires finishing"),
                            error: errors.requires_finishing,
                            touched: touched.requires_finishing,
                            checked: values.requires_finishing,
                        }, {
                            name: "requires_bundling",
                            label: gettext("Requires bundling"),
                            error: errors.requires_bundling,
                            touched: touched.requires_bundling,
                            checked: values.requires_bundling,
                        },
                    ]}
                    label={gettext("Optional stages")}
                    cssClass="offset-md-1 col-md-3"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    disabled={readOnly}
                />
                <FormGroup
                    label={gettext("Estimated duration (in hours)")}
                    name="estimated_duration"
                    type="number"
                    value={values.estimated_duration}
                    cssClass="offset-md-1 col-md-2"
                    touched={!!touched.estimated_duration}
                    error={errors.estimated_duration}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    disabled={readOnly}
                />
            </div>

            <h4 className="mb-4">{gettext("Components")}</h4>

            <FieldArray
                name="components"
                render={arrayHelpers => (
                    <div>
                        {values.components.map((component, index) => (
                            <div className="form-row mb-5 mb-md-0" key={componentKey(index)}>
                                <SelectField
                                    label={index === 0 ? gettext("Article") : null}
                                    name={componentFieldName('article', index)}
                                    value={component.article}
                                    autoload={false}
                                    cssClass="col-8 col-xl-4"
                                    loadOptions={(input, callback) => {
                                        api.profitArticleList.fetch(null, {search: input}).then(
                                            (articles) => {
                                                callback(null, {
                                                    options: articles.map(article => ({
                                                        label: `${article.code}: ${article.name}`,
                                                        value: parseInt(article.id, 10),
                                                        profit_data: article,
                                                    })),
                                                    complete: false,
                                                });
                                            },
                                            console.error,
                                        );
                                    }}
                                    touched={isComponentFieldTouched(touched, 'article', index)}
                                    error={getComponentFieldError(errors, 'article', index)}
                                    onBlur={setFieldTouched}
                                    onChange={setFieldValue}
                                    disabled={readOnly}
                                />

                                <FormGroup
                                    label={index === 0 ? gettext("Quantity") : null}
                                    name={componentFieldName('quantity', index)}
                                    type="text"
                                    onKeyPress={(event) => {
                                        const keyValue = String.fromCharCode(event.keyCode || event.which);
                                        if (!(/\d|\./.test(keyValue))) {
                                            event.preventDefault();
                                        }
                                    }}
                                    value={component.quantity}
                                    cssClass="col-3 col-xl-2"
                                    touched={isComponentFieldTouched(touched, 'quantity', index)}
                                    error={getComponentFieldError(errors, 'quantity', index)}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    disabled={readOnly}
                                />
                                <div className="col-1 text-left">
                                    {index === 0 ? <div className="mb-2">{gettext("Remove")}</div> : null}
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger" disabled={readOnly}
                                        onClick={() => readOnly || arrayHelpers.remove(index)}
                                    >
                                        <FontAwesomeIcon icon="times" />
                                    </button>
                                </div>

                            </div>
                        ))}
                        {values.components.length === 0
                            ? <div className="alert alert-warning my-4">{gettext("No components added!")}</div>
                            : null
                        }
                        <div className="mt-5 d-flex">
                            <h4 className="d-block">{gettext("Add product components")}</h4>
                            <div className="text-muted pl-2">
                                {gettext("(Loading many components takes time)")}
                            </div>
                        </div>
                        <div className="row">
                            <SelectField
                                name="addProductComponent.article"
                                value={values.addProductComponent.article}
                                autoload={false}
                                cssClass="col-8 col-xl-4"
                                loadOptions={(input, callback) => {
                                    api.profitArticleList.fetch(null, {search: input}).then(
                                        (articles) => {
                                            callback(null, {
                                                options: articles.map(article => ({
                                                    label: `${article.code}: ${article.name}`,
                                                    value: parseInt(article.id, 10),
                                                    profit_data: article,
                                                })),
                                                complete: false,
                                            });
                                        },
                                        console.error,
                                    )
                                    .then(setFieldValue('loadButtonDisabled', false));
                                }}
                                onBlur={setFieldTouched}
                                onChange={setFieldValue}
                                disabled={readOnly}
                            />
                            <div className="col-3 col-xl-2 text-center">
                                <button
                                    type="button"
                                    className="col-12 btn btn-info"
                                    disabled={
                                        isSubmitting ||
                                        values.isProductComponentAdderLoading ||
                                        values.loadButtonDisabled
                                    }
                                    onClick={() => {
                                        setFieldValue('isProductComponentAdderLoading', true);
                                        const query = {product_id: values.addProductComponent.article.value};
                                        api.productComponents.fetch(query)
                                            .then((data) => {
                                                data.map(result => arrayHelpers.push({
                                                    article: {
                                                        label: result.article.name,
                                                        value: parseInt(result.article.id, 10),
                                                        profit_data: result.profit_data,
                                                    },
                                                    quantity: result.profit_data.quantity,
                                                }));
                                                setFieldValue('isProductComponentAdderLoading', false);
                                                setFieldValue('loadButtonDisabled', false);
                                                setFieldValue('addProductComponent', {
                                                    article: {
                                                        label: '',
                                                        value: '',
                                                        profit_data: {},
                                                    },
                                                });
                                            })
                                            .catch(console.error);
                                    }}
                                >
                                    {gettext("Load")}
                                </button>
                            </div>
                            {values.isProductComponentAdderLoading &&
                            <div
                                className="col-1 pl-1 text-left"
                            >
                                <FontAwesomeIcon icon="spinner" pulse size="2x" />
                            </div>
                            }
                        </div>

                        <div className="my-4">
                            {!readOnly
                                ? <button
                                    key="add-article"
                                    type="button"
                                    className="btn btn-outline-primary"
                                    disabled={isSubmitting || values.isProductComponentAdderLoading}
                                    onClick={() => arrayHelpers.push({article: null, quantity: null})}
                                >
                                    {gettext("Add component")}
                                </button>
                                : null
                            }
                            <button
                                type="submit"
                                className="btn btn-primary ml-2"
                                disabled={isSubmitting || values.isProductComponentAdderLoading}
                            >
                                {productId ? gettext("Update product") : gettext("Create product")}
                            </button>
                        </div>
                    </div>
                )}
            />
        </form>
    );
};


class ProductForm extends React.Component {
    /* Component for creating and updating a product with it's components.
     */
    static propTypes = {
        productId: PropTypes.number,
        orderId: PropTypes.number.isRequired,
        orderRowNr: PropTypes.string.isRequired,
        welderRole: PropTypes.string.isRequired,
        initial: PropTypes.shape({
            read_only: PropTypes.bool,
            welder: PropTypes.shape({
                label: PropTypes.string.isRequired,
                value: PropTypes.number.isRequired,
            }),
            requires_finishing: PropTypes.bool.isRequired,
            requires_bundling: PropTypes.bool.isRequired,
            isProductComponentAdderLoading: PropTypes.bool.isRequired,
            loadButtonDisabled: PropTypes.bool.isRequired,
            estimated_duration: PropTypes.number,
            related: PropTypes.arrayOf(PropTypes.shape({
                id: PropTypes.number.isRequired,
                label: PropTypes.string.isRequired,
                is_checked: PropTypes.bool.isRequired,
            }).isRequired),
            components: PropTypes.arrayOf(PropTypes.shape({
                id: PropTypes.number,
                article: PropTypes.shape({
                    label: PropTypes.string.isRequired,
                    value: PropTypes.number.isRequired,
                    profit_data: PropTypes.shape().isRequired,
                }).isRequired,
                quantity: PropTypes.number.isRequired,
            })).isRequired,
            addProductComponent: PropTypes.shape({
                id: PropTypes.number,
                article: PropTypes.shape({
                    label: PropTypes.string.isRequired,
                    profit_data: PropTypes.shape().isRequired,
                }).isRequired,
            }),
        }).isRequired,
    };

    static defaultProps = {
        productId: null,
    };

    onSubmit(values, handlers) {
        let apiCall = null;

        if (this.props.productId) {
            apiCall = api.productionProductDetail.put({pk: this.props.productId},
                this.mapValuesToPostData(values, this.props.orderId));
        } else {
            const checkedRelatedOrders = values.related.filter(order => order.is_checked);
            for (let i = 0; i < checkedRelatedOrders.length; i++) {
                api.productionProductList.post(null, this.mapValuesToPostData(values, checkedRelatedOrders[i].id))
                    .catch(console.error);
            }
            apiCall = api.productionProductList.post(null, this.mapValuesToPostData(values, this.props.orderId));
        }
        apiCall.then(
            () => {
                window.location = DJ_CONST.reverse['sales:order-detail'](this.props.orderId);
            },
            (error) => {
                handlers.setSubmitting(false);

                if (error.isValidationError) {
                    const errors = this.mapServerErrors(JSON.parse(error.responseText));
                    handlers.setErrors(errors);
                } else {
                    console.error(error);
                }
            },
        );
    }

    addNonFieldError(errors, message) {
        if (!errors.nonFieldErrors) {
            errors.nonFieldErrors = [];  // eslint-disable-line no-param-reassign
        }
        errors.nonFieldErrors.push(message);
    }

    mapComponentErrors(componentErrors) {
        const errors = {};
        const errorKeys = Object.keys(componentErrors);
        for (let i = 0; i < errorKeys.length; i++) {
            const key = errorKeys[i];
            if (['name', 'profit_id', 'profit_data'].indexOf(key) >= 0) {
                errors.article = componentErrors[key][0];
            } else {
                errors[key] = componentErrors[key][0];
            }
        }
        return errors;
    }

    mapServerErrors(serverErrors) {
        const errors = {};
        const errorKeys = Object.keys(serverErrors);
        for (let i = 0; i < errorKeys.length; i++) {
            const key = errorKeys[i];
            if (key === 'components') {
                errors[key] = [];
                for (let j = 0; j < serverErrors[key].length; j++) {
                    errors[key][j] = this.mapComponentErrors(serverErrors[key][j]);
                }
            } else if (key === 'welder_id') {
                errors.welder = serverErrors[key][0];
            } else if (key === 'order_id') {
                this.addNonFieldError(errors, `${gettext('Order ID')}: ${serverErrors[key][0]}`);
            } else if (key === 'order_row_nr') {
                this.addNonFieldError(errors, `${gettext('Order row number')}: ${serverErrors[key][0]}`);
            } else if (key === 'non_field_errors') {
                this.addNonFieldError(errors, serverErrors[key][0]);
            } else {
                errors[key] = serverErrors[key][0];
            }
        }
        return errors;
    }

    mapValuesToPostData(values, orderId) {
        const data = {...values};
        data.welder_id = data.welder.value;
        data.order_id = orderId;
        data.order_row_nr = this.props.orderRowNr;
        data.components = values.components.map(component => ({
            id: component.id,
            name: component.article.label,
            profit_id: component.article.value,
            profit_data: component.article.profit_data,
            quantity: component.quantity,
        }));
        return data;
    }

    validate(values) {
        const errorRequired = gettext("This field is required.");
        const errorMinValue = (value) => {
            const text = gettext("Ensure this value is greater than or equal to %(limit_value)s.");
            return interpolate(text, {limit_value: value}, true);
        };
        const errors = {};
        if (!values.welder || !values.welder.value) {
            errors.welder = errorRequired;
        }
        if (!isNaN(parseFloat(values.estimated_duration)) && parseFloat(values.estimated_duration) < 0) {
            errors.estimated_duration = errorMinValue('0');
        }
        const addComponentError = (field, index, errorMsg) => {
            if (typeof errors.components === 'undefined') {
                errors.components = [];
            }
            for (let i = 0; i <= index; i++) {
                if (typeof errors.components[i] === 'undefined') {
                    errors.components[i] = {};
                }
            }
            errors.components[index][field] = errorMsg;
        };
        for (let i = 0; i < values.components.length; i++) {
            const component = values.components[i];
            if (!component.article || !component.article.value) {
                addComponentError('article', i, errorRequired);
            }
            if (isNaN(parseFloat(component.quantity))) {
                addComponentError('quantity', i, errorRequired);
            } else if (parseFloat(component.quantity) < 0.001) {
                addComponentError('quantity', i, errorMinValue('0.001'));
            }
        }
        return errors;
    }

    render() {
        return (
            <Formik
                initialValues={this.props.initial}
                validate={(values, props) => this.validate(values, props)}
                onSubmit={(values, handlers) => this.onSubmit(values, handlers)}
                render={formLayout(this.props.productId, this.props.welderRole, this.props.initial.read_only)}
            />
        );
    }
}

const renderProductForm = (containerId, productId, orderId, orderRowNr, welderRole, initial) => {
    const container = document.getElementById(containerId);
    if (!container) {
        return;
    }

    const props = {orderId, productId, orderRowNr, welderRole, initial};

    ReactDOM.render(
        <ProductForm {...props} />,
        container,
    );
};

export default renderProductForm;



// WEBPACK FOOTER //
// ./src/components/ProductForm.js